/**
 * Deadline-proximity score degradation — shared utility for sub-KPI types
 * that support deadline-driven scoring.
 *
 * When a deadline is configured and the goal has NOT been met, the score
 * degrades as the deadline approaches:
 *   - Plenty of time remaining     → 100  (Green — on track)
 *   - Within warning_days          →  80  (Yellow — deadline approaching)
 *   - Within critical_days         →  70  (Red-ish — urgent)
 *   - Overdue                      →   0  (hard fail)
 *
 * This matches the existing mission.ts deadline behavior so types migrating
 * to the new architecture preserve their semantics.
 *
 * Each type that uses deadline degradation should:
 *   1. Compute its base score
 *   2. Determine whether the goal has been met
 *   3. Pass both into `applyDeadlineDegradation()` if deadline is configured
 *
 * Per-instance toggle: types should expose `deadline_degradation_enabled` in
 * their config schema (default true). When disabled, the base score is
 * returned unchanged regardless of deadline proximity.
 *
 * See: DEV-814 KPI Engine Refinement, Phase 2.
 */

import { KPI_DEFAULTS } from '../defaults';

export interface DeadlineDegradationConfig {
  /** ISO date (YYYY-MM-DD) of the deadline. */
  deadline_date?: string;
  /** Days before deadline to trigger Yellow warning. Default 30. */
  warning_days?: number;
  /** Days before deadline to trigger Red critical. Default 7. */
  critical_days?: number;
  /** Master switch — if false, no deadline degradation is applied. Default true. */
  deadline_degradation_enabled?: boolean;
}

export interface DeadlineDegradationInput {
  /** The base score before degradation (0-100). */
  base_score: number;
  /** Whether the goal is already met (in which case no degradation applies). */
  goal_met: boolean;
  /** The as-of date for the scoring (YYYY-MM-DD). */
  asOfDate: string;
  /** Configuration. */
  config: DeadlineDegradationConfig;
}

export interface DeadlineDegradationResult {
  /** The post-degradation score (may equal base_score if no degradation applied). */
  score: number;
  /** Optional explanation of how degradation affected the score. */
  explain: string;
}

/**
 * Apply deadline-proximity degradation to a base score.
 *
 * Degradation rules (only applied if deadline configured AND goal not met
 * AND degradation enabled):
 *   - Overdue: score = 0
 *   - Within critical_days: score = min(base_score, 70)
 *   - Within warning_days: score = min(base_score, 80)
 *   - Otherwise: score = 100 (plenty of time)
 *
 * Note: when within warning/critical windows, we take MIN of base and the
 * threshold so a low base score isn't artificially boosted upward. This
 * matches the existing mission.ts behavior (lines 367-393).
 */
export function applyDeadlineDegradation(
  input: DeadlineDegradationInput,
): DeadlineDegradationResult {
  const { base_score, goal_met, asOfDate, config } = input;
  const enabled = config.deadline_degradation_enabled ?? true;

  // No deadline OR goal already met OR degradation disabled → base score unchanged.
  if (!enabled || goal_met || !config.deadline_date) {
    return { score: base_score, explain: '' };
  }

  const deadlineMs = new Date(config.deadline_date).getTime();
  const asOfMs = new Date(asOfDate).getTime();
  const daysLeft = (deadlineMs - asOfMs) / 86_400_000;

  const warningDays = config.warning_days ?? KPI_DEFAULTS.deadline.warning_days;
  const criticalDays = config.critical_days ?? KPI_DEFAULTS.deadline.critical_days;

  if (daysLeft < 0) {
    return {
      score: KPI_DEFAULTS.deadline.overdue_score,
      explain: `Overdue — deadline was ${config.deadline_date}.`,
    };
  }
  if (daysLeft <= criticalDays) {
    const score = Math.min(base_score, KPI_DEFAULTS.deadline.critical_score_cap);
    return {
      score,
      explain: `Critical — ${Math.ceil(daysLeft)}d until deadline ${config.deadline_date}.`,
    };
  }
  if (daysLeft <= warningDays) {
    const score = Math.min(base_score, KPI_DEFAULTS.deadline.warning_score_cap);
    return {
      score,
      explain: `Warning — ${Math.ceil(daysLeft)}d until deadline ${config.deadline_date}.`,
    };
  }

  // Plenty of time — for non-binary types we keep the base score; for binary
  // (which is 0 when not achieved) the existing behavior is to show 100 when
  // on track. Each type decides; this utility returns the base score and
  // lets the type override if its semantics differ.
  return { score: base_score, explain: '' };
}
